import { ReactNode, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, ConfigProvider, Flex, Typography } from "antd";
import { CheckOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";
import {
  payrollClassesOptions,
  payrollRecurringItemsOptions,
  payrollSettingsOptions,
  payrollStaffOptions,
} from "hooks/queries/payroll";
import FrequencyForm, {
  frequencyHint,
  getFrequencyLabels,
} from "./FrequencyForm";

// Scroll targets for steps 2 and 3: the cards below the guide carry these ids.
export const PAYROLL_CLASSES_CARD_ID = "payroll-classes";
export const PAYROLL_STAFF_CARD_ID = "payroll-staff";

// "Sembunyikan panduan" is a per-browser preference, keyed by studio. It is
// only offered once the frequency is saved, since until then the guide's
// first step is the only place to set it.
const hiddenKey = (businessId: string) => `payrollGuideHidden.${businessId}`;

const readHidden = (businessId: string) => {
  try {
    return !!businessId && localStorage.getItem(hiddenKey(businessId)) === "1";
  } catch {
    return false;
  }
};

export const usePayrollGuideHidden = (businessId: string) => {
  const [hidden, setHiddenState] = useState(() => readHidden(businessId));
  useEffect(() => setHiddenState(readHidden(businessId)), [businessId]);
  const setHidden = useCallback(
    (next: boolean) => {
      setHiddenState(next);
      try {
        if (next) localStorage.setItem(hiddenKey(businessId), "1");
        else localStorage.removeItem(hiddenKey(businessId));
      } catch {
        // Storage blocked: the choice lasts until the page reloads.
      }
    },
    [businessId]
  );
  return [hidden, setHidden] as const;
};

const scrollToCard = (id: string) => {
  const reduce = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
};

type StepState = "done" | "current" | "todo" | "locked";

const dotStyle = (state: StepState) => {
  const base = {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: textPx(12.5),
    fontWeight: 600,
    boxSizing: "border-box" as const,
  };
  if (state === "done")
    return { ...base, background: colors.success, color: colors.white };
  if (state === "current")
    return { ...base, background: colors.navy, color: colors.white };
  return {
    ...base,
    background: colors.card,
    color: colors.ink2,
    border: `1.5px solid ${colors.lineStrong}`,
  };
};

const stepButtonsTheme = {
  components: {
    Button: {
      defaultBorderColor: colors.ink3,
      defaultHoverBorderColor: colors.ink2,
      defaultHoverColor: colors.ink,
    },
  },
};

const sideTextStyle = {
  fontSize: textPx(12.5),
  color: colors.ink2,
  whiteSpace: "nowrap" as const,
};

const Step = ({
  n,
  state,
  title,
  description,
  side,
  children,
  first,
}: {
  n: number;
  state: StepState;
  title: string;
  description: string | null;
  side?: ReactNode;
  children?: ReactNode;
  first?: boolean;
}) => (
  <li
    aria-current={state === "current" ? "step" : undefined}
    style={{
      display: "grid",
      gridTemplateColumns: "26px minmax(0, 1fr) auto",
      columnGap: 14,
      padding: "14px 0",
      borderTop: first ? undefined : `1px solid ${colors.line}`,
      alignItems: "start",
    }}
  >
    <span style={dotStyle(state)}>
      {state === "done" ? <CheckOutlined style={{ fontSize: 12 }} /> : n}
    </span>
    <div style={{ minWidth: 0 }}>
      <Typography.Text
        strong
        style={{
          fontSize: textPx(14),
          color: state === "locked" ? colors.ink2 : colors.ink,
        }}
      >
        {title}
      </Typography.Text>
      {description && (
        <Typography.Paragraph
          style={{
            margin: "2px 0 0",
            fontSize: textPx(13),
            color: colors.ink2,
          }}
        >
          {description}
        </Typography.Paragraph>
      )}
      {children && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
    <div style={{ paddingTop: 2 }}>
      {state === "locked" ? (
        <span
          style={{
            ...sideTextStyle,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <LockOutlined />
          {translate("payrollPage.guide.unlock_after_step1")}
        </span>
      ) : (
        side
      )}
    </div>
  </li>
);

// First-run checklist for the Penggajian page, shown until the studio's
// first payroll run exists (or the Manager hides it). It answers the three
// questions the old single notice left open: what to do first, what comes
// next, and when payroll can be run. Step 1 IS the frequency control while
// the guide shows, so the page renders no separate Pengaturan Penggajian
// card meanwhile. Steps 2 and 3 aren't required (studios pay commissions,
// salaries or both; the description says so) and count as done once one class / one staff member is
// set; their counts come from the same queries the cards below use.
const PayrollSetupGuide = ({ onHide }: { onHide: () => void }) => {
  const navigate = useNavigate();
  const { data: settings } = useQuery(payrollSettingsOptions());
  const { data: classes = [] } = useQuery(payrollClassesOptions());
  const { data: staff = [] } = useQuery(payrollStaffOptions(false));
  const { data: items = [] } = useQuery(payrollRecurringItemsOptions());
  const [editingFrequency, setEditingFrequency] = useState(false);

  const saved = !!settings?.exists;
  const classesSet = classes.filter((c) => c.configured).length;
  // A staff member is set once they have a base salary above Rp 0 or any
  // recurring item (allowance or deduction).
  const staffWithItems = new Set(items.map((i) => i.staff_id));
  const staffSet = staff.filter(
    (s) => (s.default_basic_salary ?? 0) > 0 || staffWithItems.has(s.user_id)
  ).length;

  const done = [saved, saved && classesSet > 0, saved && staffSet > 0, false];
  const doneCount = done.filter(Boolean).length;
  const currentIndex = done.findIndex((d) => !d);
  const stateOf = (i: number): StepState => {
    if (done[i]) return "done";
    if (i > 0 && !saved) return "locked";
    return i === currentIndex ? "current" : "todo";
  };

  const countText = (key: string, n: number, total: number) =>
    total > 0 ? (
      <span style={sideTextStyle}>{translateWithVars(key, { n, total })}</span>
    ) : null;

  const showFrequencyForm = !saved || editingFrequency;

  return (
    <Card
      title={translate("payrollPage.guide.title")}
      extra={
        <Flex align="center" gap={10}>
          <span style={sideTextStyle}>
            {translateWithVars("payrollPage.guide.progress", {
              done: doneCount,
              total: 4,
            })}
          </span>
          <span
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={4}
            aria-valuenow={doneCount}
            aria-label={translate("payrollPage.guide.title")}
            style={{
              width: 120,
              height: 5,
              borderRadius: 99,
              background: colors.bgSoftHover,
              overflow: "hidden",
              display: "block",
            }}
          >
            <span
              style={{
                display: "block",
                height: "100%",
                width: `${(doneCount / 4) * 100}%`,
                background: colors.navy,
                borderRadius: 99,
              }}
            />
          </span>
        </Flex>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Typography.Paragraph
        style={{
          margin: 0,
          padding: "14px 20px 0",
          fontSize: textPx(13),
          color: colors.ink2,
          maxWidth: 760,
        }}
      >
        {translate("payrollPage.guide.description")}
      </Typography.Paragraph>
      {/* The steps' outline buttons get a darker border than the app's
          hairline default (Andrew, 2026-10-09: it read as no border at all on
          the white card). ink3 is the closest token to the 3:1 a control's
          edge needs against white. */}
      <ConfigProvider theme={stepButtonsTheme}>
        <ol style={{ listStyle: "none", margin: 0, padding: "4px 20px 6px" }}>
          <Step
            first
            n={1}
            state={stateOf(0)}
            title={
              showFrequencyForm
                ? translate("payrollPage.guide.step1_title")
                : translateWithVars("payrollPage.guide.step1_done_title", {
                    frequency: getFrequencyLabels()[settings!.frequency],
                  })
            }
            description={
              showFrequencyForm
                ? translate("payrollPage.guide.step1_desc")
                : frequencyHint(settings?.frequency)
            }
            side={
              saved && !editingFrequency ? (
                <Button
                  type="link"
                  style={{ padding: 0, height: "auto", color: colors.navy }}
                  onClick={() => setEditingFrequency(true)}
                >
                  {translate("payrollPage.guide.change")}
                </Button>
              ) : undefined
            }
          >
            {showFrequencyForm && (
              <FrequencyForm
                layout="inline"
                onSaved={() => setEditingFrequency(false)}
                onCancel={
                  editingFrequency
                    ? () => setEditingFrequency(false)
                    : undefined
                }
              />
            )}
          </Step>
          <Step
            n={2}
            state={stateOf(1)}
            title={translate("payrollPage.guide.step2_title")}
            description={translate("payrollPage.guide.step2_desc")}
            side={
              <Flex align="center" gap={12}>
                {countText(
                  "payrollPage.guide.step2_count",
                  classesSet,
                  classes.length
                )}
                <Button
                  size="small"
                  onClick={() => scrollToCard(PAYROLL_CLASSES_CARD_ID)}
                >
                  {translate("payrollPage.guide.step2_button")}
                </Button>
              </Flex>
            }
          />
          <Step
            n={3}
            state={stateOf(2)}
            title={translate("payrollPage.guide.step3_title")}
            description={translate("payrollPage.guide.step3_desc")}
            side={
              <Flex align="center" gap={12}>
                {countText(
                  "payrollPage.guide.step3_count",
                  staffSet,
                  staff.length
                )}
                <Button
                  size="small"
                  onClick={() => scrollToCard(PAYROLL_STAFF_CARD_ID)}
                >
                  {translate("payrollPage.guide.step3_button")}
                </Button>
              </Flex>
            }
          />
          <Step
            n={4}
            state={stateOf(3)}
            title={translate("payrollPage.guide.step4_title")}
            description={translate("payrollPage.guide.step4_desc")}
            side={
              <Button
                size="small"
                onClick={() =>
                  navigate(PATHS.PAYROLL_RUN_NEW, {
                    state: { from: PATHS.PAYROLL },
                  })
                }
              >
                {translate("payrollPage.run_start.title")}
              </Button>
            }
          />
        </ol>
      </ConfigProvider>
      <Flex
        justify="space-between"
        align="center"
        gap={12}
        wrap
        style={{
          padding: "10px 20px 12px",
          borderTop: `1px solid ${colors.line}`,
        }}
      >
        <span style={{ ...sideTextStyle, whiteSpace: "normal" }}>
          {translate("payrollPage.guide.footer")}
        </span>
        {saved && (
          <Button
            type="link"
            style={{ padding: 0, height: "auto", color: colors.navy }}
            onClick={onHide}
          >
            {translate("payrollPage.guide.hide")}
          </Button>
        )}
      </Flex>
    </Card>
  );
};

export default PayrollSetupGuide;
